/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

// Selectors over CommonStoreState (used with `useStore(...)`).
// Pull from `selector/index` (barrel) at call sites; this file is the canonical home.

import { CommonStoreState } from '../common';
import { MeasurementTrack } from '../../models/measurement';

export const set = (state: CommonStoreState) => state.set;

// identity / locale
export const user = (state: CommonStoreState) => state.user;
export const language = (state: CommonStoreState) => state.language;
export const locale = (state: CommonStoreState) => state.locale;
export const selectedFloatingWindow = (state: CommonStoreState) => state.selectedFloatingWindow;
export const gallerySketchMode = (state: CommonStoreState) => state.gallerySketchMode;
export const setGallerySketchMode = (state: CommonStoreState) => state.setGallerySketchMode;
export const recentlyOpenedExamples = (state: CommonStoreState) => state.recentlyOpenedExamples;
export const recentlyOpenedProjects = (state: CommonStoreState) => state.recentlyOpenedProjects;
export const recentProjectToOpen = (state: CommonStoreState) => state.recentProjectToOpen;

// proteins / molecules
export const proteinData = (state: CommonStoreState) => state.proteinData;
export const molecules = (state: CommonStoreState) => state.projectState.molecules;
export const numberOfColumns = (state: CommonStoreState) => state.projectState.numberOfColumns;
export const selectedMolecule = (state: CommonStoreState) => state.projectState.selectedMolecule;
export const selectedMoleculeNames = (state: CommonStoreState) => state.projectState.selectedMoleculeNames;
export const crystals = (state: CommonStoreState) => state.projectState.crystals;
export const selectedCrystal = (state: CommonStoreState) => state.projectState.selectedCrystal;
export const selectedCrystalNames = (state: CommonStoreState) => state.projectState.selectedCrystalNames;
export const chamberCrystal = (state: CommonStoreState) => state.projectState.chamberCrystal;
export const chamberSupercell = (state: CommonStoreState) => state.projectState.chamberSupercell;
export const chamberGuests = (state: CommonStoreState) => state.projectState.chamberGuests;
export const devices = (state: CommonStoreState) => state.projectState.devices;
export const selectedDevice = (state: CommonStoreState) => state.projectState.selectedDevice;
export const chamberDevice = (state: CommonStoreState) => state.projectState.chamberDevice;
export const generateCrystalPrompt = (state: CommonStoreState) => state.projectState.generateCrystalPrompt;
export const generateCrystalPromptHistory = (state: CommonStoreState) =>
  state.projectState.generateCrystalPromptHistory;
export const ligand = (state: CommonStoreState) => state.projectState.ligand;
export const protein = (state: CommonStoreState) => state.projectState.protein;
export const biomolecules = (state: CommonStoreState) => state.projectState.biomolecules;
export const selectedBiomoleculeName = (state: CommonStoreState) => state.projectState.selectedBiomoleculeName;
export const selectedBiomoleculeNames = (state: CommonStoreState) => state.projectState.selectedBiomoleculeNames;
// Partial-MD simulation settings (persisted config; edited in the settings popover).
export const bioSimSolvent = (state: CommonStoreState) => state.projectState.bioSimSolvent;
// Tier 2 ligand-binding study.
export const bioLigand = (state: CommonStoreState) => state.projectState.bioLigand;
export const bioLigandTransform = (state: CommonStoreState) => state.projectState.bioLigandTransform;
export const bioLigandReplaces = (state: CommonStoreState) => state.projectState.bioLigandReplaces ?? null;
// Resolved structure shown in the 3D lab: the selected entry, else the first.
export const selectedBiomolecule = (state: CommonStoreState) => {
  const list = state.projectState.biomolecules ?? [];
  const name = state.projectState.selectedBiomoleculeName;
  return (name ? list.find((m) => m.name === name) : undefined) ?? list[0] ?? null;
};
export const ligandTransform = (state: CommonStoreState) => state.projectState.ligandTransform;
export const ligandConformation = (state: CommonStoreState) => state.projectState.ligandConformation;
export const ligandFlexible = (state: CommonStoreState) => state.projectState.ligandFlexible;
export const ligandGhostVisible = (state: CommonStoreState) => state.projectState.ligandGhostVisible;
export const bindingSiteCenter = (state: CommonStoreState) => state.projectState.bindingSiteCenter;
export const pharmacophoreGuided = (state: CommonStoreState) => state.projectState.pharmacophoreGuided;
export const screeningResults = (state: CommonStoreState) => state.projectState.screeningResults;
export const screeningAnalysisResult = (state: CommonStoreState) => state.projectState.screeningAnalysisResult;
export const searchRadius = (state: CommonStoreState) => state.projectState.searchRadius;
export const searchRegionVisible = (state: CommonStoreState) => state.projectState.searchRegionVisible;
export const pocketMarkersVisible = (state: CommonStoreState) => state.projectState.pocketMarkersVisible;
export const testMolecules = (state: CommonStoreState) => state.projectState.testMolecules;

// polymer builder
export const polymerMonomerSdf = (state: CommonStoreState) => state.projectState.polymerMonomerSdf;
export const polymerMonomerName = (state: CommonStoreState) => state.projectState.polymerMonomerName;
export const polymerHeadAtomIndex = (state: CommonStoreState) => state.projectState.polymerHeadAtomIndex;
export const polymerTailAtomIndex = (state: CommonStoreState) => state.projectState.polymerTailAtomIndex;
export const polymerHeadHydrogenIndex = (state: CommonStoreState) => state.projectState.polymerHeadHydrogenIndex;
export const polymerTailHydrogenIndex = (state: CommonStoreState) => state.projectState.polymerTailHydrogenIndex;
export const polymerChainLength = (state: CommonStoreState) => state.projectState.polymerChainLength;
export const polymerTorsionJitterDeg = (state: CommonStoreState) => state.projectState.polymerTorsionJitterDeg;
export const polymerChainCount = (state: CommonStoreState) => state.projectState.polymerChainCount;
export const particleResolution = (state: CommonStoreState) => state.projectState.particleResolution;
export const polymerBeadSequence = (state: CommonStoreState) => state.projectState.polymerBeadSequence;
export const polymerBeadBondLength = (state: CommonStoreState) => state.projectState.polymerBeadBondLength;
export const polymerProperties = (state: CommonStoreState) => state.projectState.polymerProperties;
export const polymerMonomers = (state: CommonStoreState) => state.projectState.polymerMonomers;
export const polymerSequenceType = (state: CommonStoreState) => state.projectState.polymerSequenceType;
export const polymerRandomMoleFractions = (state: CommonStoreState) => state.projectState.polymerRandomMoleFractions;
export const polymerBlockLengths = (state: CommonStoreState) => state.projectState.polymerBlockLengths;
export const polymerTopology = (state: CommonStoreState) => state.projectState.polymerTopology;
export const polymerStarArmCount = (state: CommonStoreState) => state.projectState.polymerStarArmCount;
export const polymerCombSpacing = (state: CommonStoreState) => state.projectState.polymerCombSpacing;
export const polymerCombSideChainLength = (state: CommonStoreState) => state.projectState.polymerCombSideChainLength;
export const polymerBranchDensity = (state: CommonStoreState) => state.projectState.polymerBranchDensity;
export const polymerBranchLength = (state: CommonStoreState) => state.projectState.polymerBranchLength;
export const polymerDendriticInitialArms = (state: CommonStoreState) => state.projectState.polymerDendriticInitialArms;
export const polymerDendriticGenerations = (state: CommonStoreState) => state.projectState.polymerDendriticGenerations;
export const polymerDendriticBranchFactor = (state: CommonStoreState) =>
  state.projectState.polymerDendriticBranchFactor;
export const polymerDendriticArmLength = (state: CommonStoreState) => state.projectState.polymerDendriticArmLength;
export const polymerHyperbranchedProbability = (state: CommonStoreState) =>
  state.projectState.polymerHyperbranchedProbability;

// camera
export const cameraPosition = (state: CommonStoreState) => state.projectState.cameraPosition;
export const cameraRotation = (state: CommonStoreState) => state.projectState.cameraRotation;
export const cameraUp = (state: CommonStoreState) => state.projectState.cameraUp;
export const panCenter = (state: CommonStoreState) => state.projectState.panCenter;
export const navPosition = (state: CommonStoreState) => state.projectState.navPosition;
export const navRotation = (state: CommonStoreState) => state.projectState.navRotation;
export const navUp = (state: CommonStoreState) => state.projectState.navUp;
export const navTarget = (state: CommonStoreState) => state.projectState.navTarget;

// planes
export const xyPlaneVisible = (state: CommonStoreState) => state.projectState.xyPlaneVisible;
export const yzPlaneVisible = (state: CommonStoreState) => state.projectState.yzPlaneVisible;
export const xzPlaneVisible = (state: CommonStoreState) => state.projectState.xzPlaneVisible;
export const xyPlanePosition = (state: CommonStoreState) => state.projectState.xyPlanePosition;
export const yzPlanePosition = (state: CommonStoreState) => state.projectState.yzPlanePosition;
export const xzPlanePosition = (state: CommonStoreState) => state.projectState.xzPlanePosition;

// thermodynamics
export const temperature = (state: CommonStoreState) => state.projectState.temperature;
export const constantTemperature = (state: CommonStoreState) => state.projectState.constantTemperature;
export const thermostat = (state: CommonStoreState) => state.projectState.thermostat;
// Thermostat groups (region / picked-atom / element sets at their own
// setpoints) — see models/thermostatGroups.ts. Undefined ⇒ none.
export const thermostatGroups = (state: CommonStoreState) => state.projectState.thermostatGroups;
export const pressure = (state: CommonStoreState) => state.projectState.pressure;
export const constantPressure = (state: CommonStoreState) => state.projectState.constantPressure;
// Reactive MD opt-in for Molecular Modeling — routes calculateForce to
// ReactiveForceField (ReaxFF-style) instead of classical LJ + bonded.
export const reactiveMode = (state: CommonStoreState) => state.projectState.reactiveMode;

// Langevin thermostat — per-DOF stochastic thermostat (equipartitions),
// vs the default global velocity-rescale. Used when constantTemperature is on.
export const langevinThermostat = (state: CommonStoreState) => state.projectState.langevinThermostat;
export const langevinFriction = (state: CommonStoreState) => state.projectState.langevinFriction;

// Reactive mode: freeze QEq charges after the initial solve (energy-conserving)
// vs re-solve every step (non-conserving, atomizes). Undefined ⇒ on.
export const reactiveFreezeCharges = (state: CommonStoreState) => state.projectState.reactiveFreezeCharges;
export const reactiveCharges = (state: CommonStoreState) => state.projectState.reactiveCharges;
export const quadrupoleSites = (state: CommonStoreState) => state.projectState.quadrupoleSites;
export const dispersionTail = (state: CommonStoreState) => state.projectState.dispersionTail;
export const beadSolvent = (state: CommonStoreState) => state.projectState.beadSolvent;

// simulation timing
export const timeStep = (state: CommonStoreState) => state.projectState.timeStep;
export const refreshInterval = (state: CommonStoreState) => state.projectState.refreshInterval;
export const collectInterval = (state: CommonStoreState) => state.projectState.collectInterval;

// container / fields
export const molecularContainer = (state: CommonStoreState) => state.projectState.molecularContainer;
export const gravitationalAcceleration = (state: CommonStoreState) => state.projectState.gravitationalAcceleration;
export const rodFieldStrength = (state: CommonStoreState) => state.projectState.rodFieldStrength;
export const gravityDirection = (state: CommonStoreState) => state.projectState.gravityDirection;
export const pistonEnabled = (state: CommonStoreState) => state.projectState.pistonEnabled;
export const pistonPressure = (state: CommonStoreState) => state.projectState.pistonPressure;
export const pistonMass = (state: CommonStoreState) => state.projectState.pistonMass;
export const pistonLangevin = (state: CommonStoreState) => state.projectState.pistonLangevin;
export const pistonDamping = (state: CommonStoreState) => state.projectState.pistonDamping;
export const barostatEnabled = (state: CommonStoreState) => state.projectState.barostatEnabled;
export const barostatPressure = (state: CommonStoreState) => state.projectState.barostatPressure;
export const barostatTau = (state: CommonStoreState) => state.projectState.barostatTau;
export const pistonSnapshot = (state: CommonStoreState) => state.projectState.pistonSnapshot;
export const molecularContainerVisible = (state: CommonStoreState) => state.projectState.molecularContainerVisible;
export const chamberUnitCellsHidden = (state: CommonStoreState) => state.projectState.chamberUnitCellsHidden;

// graphs
export const energyGraphVisible = (state: CommonStoreState) => state.projectState.energyGraphVisible;
export const speedGraphVisible = (state: CommonStoreState) => state.projectState.speedGraphVisible;
export const irSpectrumPanelVisible = (state: CommonStoreState) => state.projectState.irSpectrumPanelVisible;
export const analysisPanelVisible = (state: CommonStoreState) => state.projectState.analysisPanelVisible;
export const activeAnalysisTab = (state: CommonStoreState) => state.projectState.activeAnalysisTab;
// Stable empty default so old projectStates (persisted before measurementTracks
// existed) don't return a fresh [] each call and thrash the selector.
const EMPTY_MEASUREMENT_TRACKS: MeasurementTrack[] = [];
export const measurementTracks = (state: CommonStoreState) =>
  state.projectState.measurementTracks ?? EMPTY_MEASUREMENT_TRACKS;
export const speedGraphMaxX = (state: CommonStoreState) => state.projectState.speedGraphMaxX;
export const speedGraphMaxY = (state: CommonStoreState) => state.projectState.speedGraphMaxY;
export const speedGraphSortByMolecule = (state: CommonStoreState) => state.projectState.speedGraphSortByMolecule;
export const speedGraphBinNumber = (state: CommonStoreState) => state.projectState.speedGraphBinNumber;

// bonds
export const vdwBondsVisible = (state: CommonStoreState) => state.projectState.vdwBondsVisible;
export const hBondsVisible = (state: CommonStoreState) => state.projectState.hBondsVisible;
export const disulfideBondsVisible = (state: CommonStoreState) => state.projectState.disulfideBondsVisible;
export const pharmacophoreVisible = (state: CommonStoreState) => state.projectState.pharmacophoreVisible;
export const vdwBondCutoffRelative = (state: CommonStoreState) => state.projectState.vdwBondCutoffRelative;
export const angularBondsMap = (state: CommonStoreState) => state.angularBondsMap;
export const angularBondsVisible = (state: CommonStoreState) => state.projectState.angularBondsVisible;
export const torsionalBondsMap = (state: CommonStoreState) => state.torsionalBondsMap;
export const torsionalBondsVisible = (state: CommonStoreState) => state.projectState.torsionalBondsVisible;

// visualization scaling
export const momentumVisible = (state: CommonStoreState) => state.projectState.momentumVisible;
export const momentumScaleFactor = (state: CommonStoreState) => state.projectState.momentumScaleFactor;
export const forceVisible = (state: CommonStoreState) => state.projectState.forceVisible;
export const forceScaleFactor = (state: CommonStoreState) => state.projectState.forceScaleFactor;
export const kineticEnergyScaleFactor = (state: CommonStoreState) => state.projectState.kineticEnergyScaleFactor;
export const kineticEnergyColorVisible = (state: CommonStoreState) => state.projectState.kineticEnergyColorVisible;

// properties view
export const selectedProperty = (state: CommonStoreState) => state.projectState.selectedProperty;
export const autoscaleGraph = (state: CommonStoreState) => state.projectState.autoscaleGraph;

// molecule CRUD
export const hasMolecule = (state: CommonStoreState) => state.hasMolecule;
export const addMolecule = (state: CommonStoreState) => state.addMolecule;
export const addMolecules = (state: CommonStoreState) => state.addMolecules;
export const removeMolecule = (state: CommonStoreState) => state.removeMolecule;
export const removeMoleculeByName = (state: CommonStoreState) => state.removeMoleculeByName;
export const removeAllMolecules = (state: CommonStoreState) => state.removeAllMolecules;
export const setMoleculeData = (state: CommonStoreState) => state.setMoleculeData;
export const setMoleculeAutoBond = (state: CommonStoreState) => state.setMoleculeAutoBond;
export const renameMolecule = (state: CommonStoreState) => state.renameMolecule;

// atom-level operations
export const deleteAllAtoms = (state: CommonStoreState) => state.deleteAllAtoms;
export const getAtomByIndex = (state: CommonStoreState) => state.getAtomByIndex;
export const fixAtomByIndex = (state: CommonStoreState) => state.fixAtomByIndex;
export const restrainAtomByIndex = (state: CommonStoreState) => state.restrainAtomByIndex;
export const dampAtomByIndex = (state: CommonStoreState) => state.dampAtomByIndex;
export const chargeAtomByIndex = (state: CommonStoreState) => state.chargeAtomByIndex;
export const setAtomTrajectoryByIndex = (state: CommonStoreState) => state.setAtomTrajectoryByIndex;

// derived maps
export const molecularPropertiesMap = (state: CommonStoreState) => state.molecularPropertiesMap;
export const molecularStructureMap = (state: CommonStoreState) => state.molecularStructureMap;

// undo
export const undoManager = (state: CommonStoreState) => state.undoManager;
export const addUndoable = (state: CommonStoreState) => state.addUndoable;

// logging
export const loggable = (state: CommonStoreState) => state.loggable;
export const logAction = (state: CommonStoreState) => state.logAction;

// chemical-element data
export const loadChemicalElements = (state: CommonStoreState) => state.loadChemicalElements;
export const chemicalElements = (state: CommonStoreState) => state.chemicalElements;
export const getChemicalElement = (state: CommonStoreState) => state.getChemicalElement;
export const providedMolecularProperties = (state: CommonStoreState) => state.providedMolecularProperties;
export const getProvidedMolecularProperties = (state: CommonStoreState) => state.getProvidedMolecularProperties;
export const loadProvidedMolecularProperties = (state: CommonStoreState) => state.loadProvidedMolecularProperties;

// crystal-properties table (static — populated at store init from CRYSTAL_PROPERTIES)
export const providedCrystalProperties = (state: CommonStoreState) => state.providedCrystalProperties;
export const getProvidedCrystalProperties = (state: CommonStoreState) => state.getProvidedCrystalProperties;

// project metadata
export const projectType = (state: CommonStoreState) => state.projectState.type;
export const projectTitle = (state: CommonStoreState) => state.projectState.title;
export const projectOwner = (state: CommonStoreState) => state.projectState.owner;
export const projectDescription = (state: CommonStoreState) => state.projectState.description;
export const projectSortDescending = (state: CommonStoreState) => state.projectState.sortDescending;
export const projectDataColoring = (state: CommonStoreState) => state.projectState.dataColoring;
export const projectFilters = (state: CommonStoreState) => state.projectState.filters;
export const enableFilters = (state: CommonStoreState) => state.projectState.enableFilters;
export const projectRanges = (state: CommonStoreState) => state.projectState.ranges;
export const hiddenProperties = (state: CommonStoreState) => state.projectState.hiddenProperties;
export const hiddenCrystalProperties = (state: CommonStoreState) => state.projectState.hiddenCrystalProperties;

// scatter plot
export const xAxisNameScatterPlot = (state: CommonStoreState) => state.projectState.xAxisNameScatterPlot;
export const yAxisNameScatterPlot = (state: CommonStoreState) => state.projectState.yAxisNameScatterPlot;
export const xFormula = (state: CommonStoreState) => state.projectState.xFormula;
export const yFormula = (state: CommonStoreState) => state.projectState.yFormula;
export const xMinScatterPlot = (state: CommonStoreState) => state.projectState.xMinScatterPlot;
export const xMaxScatterPlot = (state: CommonStoreState) => state.projectState.xMaxScatterPlot;
export const yMinScatterPlot = (state: CommonStoreState) => state.projectState.yMinScatterPlot;
export const yMaxScatterPlot = (state: CommonStoreState) => state.projectState.yMaxScatterPlot;
export const xLinesScatterPlot = (state: CommonStoreState) => state.projectState.xLinesScatterPlot;
export const yLinesScatterPlot = (state: CommonStoreState) => state.projectState.yLinesScatterPlot;
export const lineWidthScatterPlot = (state: CommonStoreState) => state.projectState.lineWidthScatterPlot;
export const dotSizeScatterPlot = (state: CommonStoreState) => state.projectState.dotSizeScatterPlot;
export const sortDataScatterPlot = (state: CommonStoreState) => state.projectState.sortDataScatterPlot;

// regression
export const regressionDegree = (state: CommonStoreState) => state.projectState.regressionDegree;
export const numberOfMostSimilarMolecules = (state: CommonStoreState) =>
  state.projectState.numberOfMostSimilarMolecules;

// gallery / viewers
export const hideGallery = (state: CommonStoreState) => state.projectState.hideGallery;
export const chamberViewerPercentWidth = (state: CommonStoreState) => state.projectState.chamberViewerPercentWidth;
export const chamberViewerBackground = (state: CommonStoreState) => state.projectState.chamberViewerBackground;
export const chamberViewerAxes = (state: CommonStoreState) => state.projectState.chamberViewerAxes;
export const chamberViewerFoggy = (state: CommonStoreState) => state.projectState.chamberViewerFoggy;
export const chamberViewerSelector = (state: CommonStoreState) => state.projectState.chamberViewerSelector;
export const chamberViewerColoring = (state: CommonStoreState) => state.projectState.chamberViewerColoring;
export const chamberViewerMaterial = (state: CommonStoreState) => state.projectState.chamberViewerMaterial;
export const chamberViewerStyle = (state: CommonStoreState) => state.projectState.chamberViewerStyle;
export const chamberTemperatureKevin = (state: CommonStoreState) => state.projectState.chamberTemperatureKevin;
export const projectViewerStyle = (state: CommonStoreState) => state.projectState.projectViewerStyle;
export const projectViewerOverlayStyle = (state: CommonStoreState) => state.projectState.projectViewerOverlayStyle;
export const proteinOverlayStyle = (state: CommonStoreState) => state.projectState.proteinOverlayStyle;
export const projectViewerMaterial = (state: CommonStoreState) => state.projectState.projectViewerMaterial;
export const projectViewerTranslucentOpacity = (state: CommonStoreState) =>
  state.projectState.projectViewerTranslucentOpacity;
export const projectViewerBackground = (state: CommonStoreState) => state.projectState.projectViewerBackground;

// graph / label types
export const graphType = (state: CommonStoreState) => state.projectState.graphType;
export const bioChartType = (state: CommonStoreState) => state.projectState.bioChartType;
export const labelType = (state: CommonStoreState) => state.projectState.labelType;
export const galleryViewMode = (state: CommonStoreState) => state.projectState.galleryViewMode;
export const propertiesPaneFraction = (state: CommonStoreState) => state.projectState.propertiesPaneFraction;
export const propertiesPaneCollapsed = (state: CommonStoreState) => state.projectState.propertiesPaneCollapsed;
export const comparedMoleculeNames = (state: CommonStoreState) => state.projectState.comparedMoleculeNames;
export const comparedCrystalNames = (state: CommonStoreState) => state.projectState.comparedCrystalNames;

// spaceship
export const spaceshipDisplayMode = (state: CommonStoreState) => state.projectState.spaceshipDisplayMode;
export const spaceshipThrust = (state: CommonStoreState) => state.projectState.spaceshipThrust;
export const spaceshipSize = (state: CommonStoreState) => state.projectState.spaceshipSize;
export const spaceshipRoll = (state: CommonStoreState) => state.projectState.spaceshipRoll;
export const spaceshipPitch = (state: CommonStoreState) => state.projectState.spaceshipPitch;
export const spaceshipYaw = (state: CommonStoreState) => state.projectState.spaceshipYaw;
export const spaceshipX = (state: CommonStoreState) => state.projectState.spaceshipX;
export const spaceshipY = (state: CommonStoreState) => state.projectState.spaceshipY;
export const spaceshipZ = (state: CommonStoreState) => state.projectState.spaceshipZ;

// rotation / translation steps
export const rotationStep = (state: CommonStoreState) => state.projectState.rotationStep;
export const translationStep = (state: CommonStoreState) => state.projectState.translationStep;

// nav / instruction
export const showInstructionPanel = (state: CommonStoreState) => state.projectState.showInstructionPanel;
export const navigationView = (state: CommonStoreState) => state.projectState.navigationView;

// AI / prompts
export const reasoningEffort = (state: CommonStoreState) => state.projectState.reasoningEffort;
export const aiProvider = (state: CommonStoreState) => state.projectState.aiProvider;
export const agentMode = (state: CommonStoreState) => state.projectState.agentMode;
export const numberOfStepsToRemember = (state: CommonStoreState) => state.projectState.numberOfStepsToRemember;
export const showPrompts = (state: CommonStoreState) => state.projectState.showPrompts;
export const generateMoleculePrompt = (state: CommonStoreState) => state.projectState.generateMoleculePrompt;
export const generateMoleculePromptHistory = (state: CommonStoreState) =>
  state.projectState.generateMoleculePromptHistory;
export const generatedMolecularProperties = (state: CommonStoreState) =>
  state.projectState.generatedMolecularProperties;
export const chatMessages = (state: CommonStoreState) => state.projectState.chatMessages;
